import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react';
import moment from 'moment';
import { createRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../app-state/slices/AuthSlice';
import { RootState, useAppDispatch } from '../../app-state/store';
import { restoreSession as AuthRestoreSession } from '../../canopy-ui-components/api/auth';
import CanopyButton from '../../canopy-ui-components/components/CanopyButton/CanopyButton';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
import { IdleTimer } from './IdleTimer';

const ROLES = {
    Customer: 'Customer',
    ReadAdmin: 'ReadAdmin',
    Superadmin: 'Superadmin',
    Partneradmin: 'Partneradmin',
    RelationshipManager: 'RelationshipManager',
};

const UserInactivity: React.FC<{ t: any; children: any }> = ({
    t,
    children,
}) => {
    const [idleTimeout, setIdleTimeout] = useState(0);
    const [idleMinute, setIdleMinute] = useState(0);
    const [remainingTime, setRemainingTime] = useState<number>(30);
    const [timeInterval, setTimeInterval] = useState<NodeJS.Timeout>();
    const [isIdle, setIsIdle] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const idleTimer: any = createRef();
    const dispatch = useAppDispatch();
    const router = useNavigate();
    const currentColors = useCurrentColors();
    const { user, logoutStatus } = useSelector(
        (state: RootState) => state.auth
    );

    const handleOnActive = () => {
        setIsIdle(false);
    };
    const handleOnIdle = () => {
        setIsIdle(true);
    };
    const handleOnAction = () => {};

    const handleLogout = () => {
        timeInterval && clearInterval(timeInterval);
        dispatch(logout());
    };

    const handleContinue = () => {
        timeInterval && clearInterval(timeInterval);
        setIsOpen(false);
        setIsIdle(false);
        idleTimer?.current?.reset();
    };

    useEffect(() => {
        let minutes = 1000;
        if (user && process.env.REACT_APP_ADMIN_INACTIVITY_TIMER) {
            minutes = parseInt(process.env.REACT_APP_ADMIN_INACTIVITY_TIMER);
            if (
                user?.role === ROLES.Customer &&
                process.env.REACT_APP_USER_INACTIVITY_TIMER
            ) {
                minutes = parseInt(process.env.REACT_APP_USER_INACTIVITY_TIMER);
            }
        }

        if (user && !idleTimeout) {
            setIdleTimeout(
                moment.duration(minutes, 'm').as('ms') -
                    moment.duration(30, 's').as('ms')
            );
            setIdleMinute(minutes);
            idleTimer?.current?.reset();
        }
    }, [user]);

    useEffect(() => {
        (async () => {
            if (isIdle) {
                try {
                    await AuthRestoreSession();
                    setIsOpen(isIdle);
                    setRemainingTime(30);
                    setTimeInterval(
                        setInterval(() => {
                            if (remainingTime >= 1) {
                                setRemainingTime(
                                    (remainingTime) => remainingTime - 1
                                );
                            } else {
                                if (timeInterval) {
                                    clearInterval(timeInterval);
                                }
                                handleLogout();
                            }
                        }, 1000)
                    );
                } catch (e) {
                    router('/login');
                }
            }
        })();
    }, [isIdle]);

    useEffect(() => {
        if (remainingTime < 1) {
            timeInterval && clearInterval(timeInterval);
            handleLogout();
        }
    }, [remainingTime]);

    useEffect(() => {
        return () => timeInterval && clearInterval(timeInterval);
    }, [false]);

    const isMicroFrontend = (window as any).skeleton;

    if (!idleTimeout) {
        return null;
    }

    return (
        <>
            {isMicroFrontend ? (
                children
            ) : (
                <IdleTimer
                    ref={idleTimer}
                    key="idleTimer"
                    startOnMount={false}
                    element={document}
                    onAction={handleOnAction}
                    onActive={handleOnActive}
                    onIdle={handleOnIdle}
                    timeout={idleTimeout}
                >
                    {children}
                </IdleTimer>
            )}
            <Modal
                isCentered
                size={'xl'}
                isOpen={isOpen}
                onClose={() => {
                    console.log('Sorry, Cannot close');
                }}
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader
                        fontSize="16px"
                        color={currentColors.brand['10']}
                        fontWeight="bold"
                        padding="15px"
                    >
                        {t(
                            'ui.mm.login-password-session.session-inactive-time-minute',
                            {
                                count: idleMinute,
                            }
                        )}
                    </ModalHeader>
                    <ModalBody color={currentColors.brand['10']}>
                        <Text mb="1rem">
                            {t(
                                'ui.mm.login-password-session.security-sign-out-time-second',
                                { count: remainingTime }
                            )}
                        </Text>
                    </ModalBody>

                    <ModalFooter>
                        <CanopyButton
                            fontSize="sm"
                            variant="outline"
                            mr={3}
                            onClick={handleLogout}
                        >
                            {t('logout')}
                        </CanopyButton>
                        <CanopyButton
                            fontSize="sm"
                            onClick={handleContinue}
                        >
                            {t('login-password-session.check-continue-session')}
                        </CanopyButton>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};
export default UserInactivity;
